import { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import type { Clients } from "src/contexts/AppContext";
import cleanFirestoreDoc from "src/firebase/cleanFirestoreDoc";
import isPartnerGuard from "src/shared/utils/validation/isPartnerGuard";
import { Partner } from "src/types/Partner";

export default async function fetchPartner(
  partnerId: string,
  { db }: Clients
): Promise<Partner> {
  const res = await getDoc(doc(db, "partners", partnerId));

  if (!res.exists) throw new Error("Partner not found");

  const partner = cleanFirestoreDoc(res);

  if (!isPartnerGuard(partner)) throw new Error("Malformed data");

  return partner;
}

export function getPartnerDashboardLabel(partner: Partner): string {
  return (
    partner.dashboardLabel ?? `${partner.firstName} ${partner.lastName}`.trim()
  );
}

function comparePartnerDashboards(
  ownerUid: string,
  a: Partner,
  b: Partner
): number {
  if (a.uid === ownerUid && b.uid !== ownerUid) return -1;
  if (b.uid === ownerUid && a.uid !== ownerUid) return 1;

  const aOrder = a.dashboardOrder ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.dashboardOrder ?? Number.MAX_SAFE_INTEGER;
  return (
    aOrder - bOrder ||
    getPartnerDashboardLabel(a).localeCompare(getPartnerDashboardLabel(b)) ||
    a.uid.localeCompare(b.uid)
  );
}

type PartnerIdentity = Pick<User, "uid" | "email" | "emailVerified">;

/**
 * Resolve every partner record a Firebase identity may access.
 *
 * A partner created through the public signup flow still owns `partners/{uid}`.
 * Admin-created shared dashboards may instead grant a verified Firebase email
 * access through either the primary `email` field or `authorizedEmails`.
 */
export async function resolvePartnersForIdentity(
  user: PartnerIdentity,
  { db }: Pick<Clients, "db">
): Promise<Partner[]> {
  const primary = await getDoc(doc(db, "partners", user.uid));
  const matches = new Map<string, Partner>();

  if (primary.exists()) {
    const partner = cleanFirestoreDoc(primary);
    if (isPartnerGuard(partner)) matches.set(partner.uid, partner);
  }

  const email = user.email?.trim().toLowerCase();
  // Partner-document ownership is sufficient for legacy password accounts.
  // Cross-dashboard access requires the email-link flow's verified claim,
  // matching the Firestore rule and callable checks.
  if (!email || !user.emailVerified) {
    return [...matches.values()];
  }

  const [primaryEmailMatches, authorizedEmailMatches] = await Promise.all([
    getDocs(query(collection(db, "partners"), where("email", "==", email))),
    getDocs(
      query(
        collection(db, "partners"),
        where("authorizedEmails", "array-contains", email)
      )
    ),
  ]);

  [...primaryEmailMatches.docs, ...authorizedEmailMatches.docs].forEach(
    (snapshot) => {
      const partner = cleanFirestoreDoc(snapshot);
      if (isPartnerGuard(partner)) matches.set(partner.uid, partner);
    }
  );

  return [...matches.values()].sort((a, b) =>
    comparePartnerDashboards(user.uid, a, b)
  );
}

/** The first partner is retained as session metadata for legacy consumers. */
export async function resolvePartnerForIdentity(
  user: PartnerIdentity,
  clients: Pick<Clients, "db">
): Promise<Partner | undefined> {
  return (await resolvePartnersForIdentity(user, clients))[0];
}

/** Resolve all partner dashboards backing a signed-in Firebase user. */
export async function fetchPartnersForAuthenticatedUser(
  user: PartnerIdentity,
  clients: Pick<Clients, "db">
): Promise<Partner[]> {
  const partners = await resolvePartnersForIdentity(user, clients);
  if (partners.length === 0) throw new Error("Partner not found");
  return partners;
}
